// Necessary because Astrea's default acitivity types is not on the right sorting order and has "EVENT",
// so we need this array that follows dashboard chart's sorting and axis rules
import { CaseTypeFilter } from "@customTypes/components/filters";
import * as moment from "moment";
import { theme } from "nectar-ui";

export const QUERY_STATUS = {
  loading: "IS_LOADING",
  success: "IS_SUCCESS",
  refeching: "IS_REFETCHING",
};

export const DASHBOARD_ACTIVITY_TYPES_DICTIONARY = {
  TASK: {
    label: "Tarefas",
    value: "TASK",
  },
  HEARING: {
    label: "Audiências",
    value: "HEARING",
  },
  APPOINTMENT: {
    label: "Eventos",
    value: "APPOINTMENT",
  },
  DEADLINE: {
    label: "Prazos",
    value: "DEADLINE",
  },
  ALL: {
    label: "Todas as atividades",
    value: "ALL",
  },
} as const;

export type DashboardActivityTypesKeys = keyof typeof DASHBOARD_ACTIVITY_TYPES_DICTIONARY;

export const CHART_COLORS_BY_ACTIVITY = {
  TASK: theme.nectar.colors.blue500,
  APPOINTMENT: theme.nectar.colors.green500,
  EVENT: theme.nectar.colors.green500,
  DEADLINE: theme.nectar.colors.red400,
  HEARING: theme.nectar.colors.yellow500,
};

export const CHART_COLORS_PALLETE = [
  theme.nectar.colors.blue500,
  theme.nectar.colors.green500,
  theme.nectar.colors.brown400,
  theme.nectar.colors.purple300,
  theme.nectar.colors.red400,
  theme.nectar.colors.pink500,
  theme.nectar.colors.green700,
];

export const LAWSUIT_COUNTER_FILTERS = {
  ACTIVE_LAWSUIT: {
    queryParam: "ACTIVE_LAWSUIT",
    label: "Processos e casos ativos",
  },
  CREATED_DATE: {
    queryParam: "CREATED_DATE",
    label: "Processos e casos cadastrados",
  },
  ENDED_DATE: {
    queryParam: "ENDED_DATE",
    label: "Processos e casos encerrados",
  },
};

export const DASHBOARD_CASE_TYPES: Record<string, CaseTypeFilter> = {
  LAWSUIT: {
    queryParam: ["CTE_LAWSUIT"],
    label: "Processos",
  },
  CASE: {
    queryParam: ["CTE_CASE"],
    label: "Casos",
  },
  ALL: {
    queryParam: ["CTE_LAWSUIT", "CTE_CASE"],
    label: "Processos e casos",
  },
};

export const PAST_ALLOWED_RANGE_DEFAULT = [
  "TODAY",
  "LAST_7_DAYS",
  "LAST_30_DAYS",
  "LAST_60_DAYS",
  "LAST_90_DAYS",
];

export const FUTURE_ALLOWED_RANGE_DEFAULT = ["THIS_WEEK", "THIS_MONTH", "NEXT_WEEK", "NEXT_MONTH"];

export const DATE_RANGE_MAP = {
  TODAY: "TODAY",
  LAST_WEEK: "LAST_7_DAYS",
  LAST_MONTH: "LAST_30_DAYS",
  LAST_TWO_MONTHS: "LAST_60_DAYS",
  LAST_THREE_MONTHS: "LAST_90_DAYS",
  LAST_YEAR: "LAST_YEAR",
  THIS_WEEK: "THIS_WEEK",
  THIS_MONTH: "THIS_MONTH",
  NEXT_WEEK: "NEXT_WEEK",
  NEXT_MONTH: "NEXT_MONTH",
} as const;

export const FULL_RANGE = Object.values(DATE_RANGE_MAP);
export type FULL_DATE_RANGE_KEYS = keyof typeof DATE_RANGE_MAP;

export type ALL_ALLOWED_DATE_RANGES = (typeof FUTURE_ALLOWED_RANGE_DEFAULT)[number] &
  keyof typeof DATE_RANGE_MAP &
  (typeof PAST_ALLOWED_RANGE_DEFAULT)[number];

export const getCustomHistogramInterval = (numberDifferenceDays: number) => {
  if (numberDifferenceDays <= 30) return "DAY";
  if (numberDifferenceDays <= 90) return "WEEK";
  return "MONTH";
};

export const DATE_RANGE_DEFAULT_HISTOGRAM_INTERVAL = {
  LAST_7_DAYS: "DAY",
  LAST_30_DAYS: "DAY",
  LAST_60_DAYS: "DAY",
  LAST_90_DAYS: "WEEK",
  LAST_YEAR: "MONTH",
};

export const DATE_RANGE_DEFAULT_HISTOGRAM_INTERVAL_FOR_MOBILE_DEVICES = {
  LAST_7_DAYS: "DAY",
  LAST_30_DAYS: "WEEK",
  LAST_60_DAYS: "WEEK",
  LAST_90_DAYS: "WEEK",
  LAST_YEAR: "MONTH",
};

export const DATE_RANGE_DEFAULT_HISTOGRAM_INTERVAL_TOOLTIP_TEXT = {
  LAST_7_DAYS: "Visualização por dia",
  LAST_30_DAYS: "Visualização por dia",
  LAST_60_DAYS: "Visualização por semana",
  LAST_90_DAYS: "Visualização por semana",
  LAST_YEAR: "Visualização por mês",
};

export type DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES =
  keyof typeof DATE_RANGE_DEFAULT_HISTOGRAM_INTERVAL_TOOLTIP_TEXT;

// TODO: Aplicar Trasnlate na Label
export const DASHBOARD_RANGE_TYPES_DICTIONARY = {
  TODAY: {
    label: "Hoje",
    range: {
      fromDate: moment().format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    },
  },
  LAST_7_DAYS: {
    label: "Últimos 7 dias",
    range: {
      fromDate: moment().subtract(6, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    },
  },
  LAST_30_DAYS: {
    label: "Últimos 30 dias",
    range: {
      fromDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    },
  },
  LAST_60_DAYS: {
    label: "Últimos 60 dias",
    range: {
      fromDate: moment().subtract(59, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    },
  },
  LAST_90_DAYS: {
    label: "Últimos 90 dias",
    range: {
      fromDate: moment().subtract(89, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    },
  },
  LAST_YEAR: {
    label: "Último ano",
    range: {
      fromDate: moment().subtract(1, "year").add(1, "day").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    },
  },
  THIS_WEEK: {
    label: "Esta semana",
    range: {
      fromDate: moment().startOf("week").format("YYYY-MM-DD"),
      toDate: moment().endOf("week").format("YYYY-MM-DD"),
    },
  },
  NEXT_WEEK: {
    label: "Próxima semana",
    range: {
      fromDate: moment().add(1, "week").startOf("week").format("YYYY-MM-DD"),
      toDate: moment().add(1, "week").endOf("week").format("YYYY-MM-DD"),
    },
  },
  THIS_MONTH: {
    label: "Este mês",
    range: {
      fromDate: moment().startOf("month").format("YYYY-MM-DD"),
      toDate: moment().endOf("month").format("YYYY-MM-DD"),
    },
  },
  NEXT_MONTH: {
    label: "Próximo mês",
    range: {
      fromDate: moment().add(1, "month").startOf("month").format("YYYY-MM-DD"),
      toDate: moment().add(1, "month").endOf("month").format("YYYY-MM-DD"),
    },
  },
};

export type DASHBOARD_RANGE_TYPE_KEYS = keyof typeof DASHBOARD_RANGE_TYPES_DICTIONARY;

export const DASHBOARD_CHART_TYPES = {
  ACTIVE_LAWSUITS_BY_RESPONSIBLE: {
    module: "lawsuit",
    chart: "active-lawsuits-by-responsible",
  },
  LAWSUIT_BY_CUSTOMER_OVER_TIME: {
    module: "lawsuit",
    chart: "lawsuit-by-customer-over-time",
  },
  LAWSUIT_COUNTER: {
    module: "lawsuit",
    chart: "lawsuit-counter",
  },
  LAWSUIT_TRACKER: {
    module: "lawsuit",
    chart: "lawsuit-tracker-line-chart",
  },
  LAWSUIT_UPDATES_COUNTER: {
    module: "lawsuit",
    chart: "lawsuit-updates-counter",
  },
  LAWSUIT_UPDATES_RADAR: {
    module: "lawsuit",
    chart: "lawsuit-updates-radar",
  },
  DONE_ACTIVITIES_BY_MONTH: {
    module: "calendar",
    chart: "done-activities-by-month",
  },
  DONE_ACTIVITIES_BY_RESPONSIBLE: {
    module: "calendar",
    chart: "done-activities-by-responsible",
  },
  IN_PROGRESS_ACTIVITIES_BY_RESPONSIBLE: {
    module: "calendar",
    chart: "in-progress-by-responsible",
  },
  LATE_ACTIVIES_COUNTER: {
    module: "calendar",
    chart: "late-activities-counter",
  },
};

export const DASHBOARD_CHART_VISUALIZATION_TYPES = {
  LINE: {
    label: "Gráfico em linhas",
    value: "LINE",
  },
  VERTICAL_BAR: {
    label: "Gráfico em barras verticais",
    value: "VERTICAL_BAR",
  },
  HORIZONTAL_BAR: {
    label: "Gráfico em barras horizontais",
    value: "HORIZONTAL_BAR",
  },
} as const;

export type DashboardChartVisualizationKeys = keyof typeof DASHBOARD_CHART_VISUALIZATION_TYPES;

export type ChartTypeFilterOption =
  (typeof DASHBOARD_CHART_VISUALIZATION_TYPES)[DashboardChartVisualizationKeys];

export const BAR_CHART_VISUALIZATION_OPTIONS: ChartTypeFilterOption[] = [
  DASHBOARD_CHART_VISUALIZATION_TYPES.VERTICAL_BAR,
  DASHBOARD_CHART_VISUALIZATION_TYPES.HORIZONTAL_BAR,
];

export const BAR_AND_LINE_CHART_VISUALIZATION_OPTIONS: ChartTypeFilterOption[] = [
  DASHBOARD_CHART_VISUALIZATION_TYPES.LINE,
  DASHBOARD_CHART_VISUALIZATION_TYPES.VERTICAL_BAR,
  DASHBOARD_CHART_VISUALIZATION_TYPES.HORIZONTAL_BAR,
];

export const CHART_ORIENTATIONS = {
  VERTICAL: "vertical",
  HORIZONTAL: "horizontal",
} as const;
